/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col } from '@design-system/components/grid/grid';

import { TimePicker } from '@mui/x-date-pickers';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GlobalStyles } from '@mui/material';
import { fontFamily, space } from '@design-system/tokens';
import { ErrorMessage } from '@client/new/components/Label';
import { FieldPath } from 'react-hook-form';
import { ChallengeForm } from '../../view-model/ChallengeForm.schema';
import { ChallengeFormVM } from '../../view-model/ChallengeForm.viewModel';

const GlobalTimePickerStyles = () => (
  <GlobalStyles
    styles={{
      '.MuiDialogActions-root': {
        padding: `${space.s} !important`,
        fontFamily: fontFamily.visuelt,
      },
    }}
  />
);

type Prefix =
  | 'registrationStart'
  | 'registrationEnd'
  | 'completionStart'
  | 'completionEnd';

const toHourDate = (
  p: Pick<
    ChallengeForm,
    | 'completionStart'
    | 'completionEnd'
    | 'registrationStart'
    | 'registrationEnd'
  > & { prefix: Prefix },
) => {
  const d = p[p.prefix];
  if (d.dateDayTime === undefined) return null;
  let hour = d.dateHour;
  if (d.dateMinute === undefined) return null;
  const minute = d.dateMinute;
  const dt = d.dateDayTime;
  if (!dt) return null;
  if (dt === 'PM' && hour !== 12) hour += 12;
  else if (dt === 'AM' && hour === 12) hour = 0;

  const expirationDate = new Date();
  expirationDate.setHours(hour);
  expirationDate.setMinutes(minute);
  expirationDate.setSeconds(0);
  return expirationDate;
};

export const TimeInput = (
  p: Pick<ChallengeFormVM, 'register' | 'values'> & {
    width?: number;
    prefix: Prefix;
  },
) => {
  type Keys = typeof p.values;
  const callChange = <T extends FieldPath<Keys>>(name: T, value: unknown) =>
    p.register(name).onChange({ target: { name, value } } as any); // TODO: fix any

  const error =
    p.register(`${p.prefix}.dateHour`)?.error ||
    p.register(`${p.prefix}.dateMinute`)?.error ||
    p.register(`${p.prefix}.dateDayTime`)?.error;

  const value = toHourDate({ prefix: p.prefix, ...p.values });
  return (
    <>
      <GlobalTimePickerStyles />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Col
          style={{
            marginTop: '1px',
            ...(p.width ? { width: p.width + 'px' } : {}),
          }}
        >
          <TimePicker
            minTime={new Date(0, 0, 0, 0, 0)}
            maxTime={new Date(0, 0, 0, 23, 59)}
            ampmInClock
            sx={{
              fontFamily: fontFamily.visuelt,

              '>div': {
                fontFamily: fontFamily.visuelt,

                height: '45px',
                borderWidth: '2px',
                borderRadius: '10px',
                borderColor: '#e4e4e4',
              },
            }}
            slotProps={{
              popper: {
                sx: {
                  '& .MuiPickersMonth-monthButton': {
                    fontFamily: fontFamily.visuelt,
                  },
                  '& .MuiPickersYear-yearButton': {
                    fontFamily: fontFamily.visuelt,
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    fontFamily: fontFamily.visuelt,
                  },
                },
              },

              textField: {
                InputProps: { sx: { fontFamily: fontFamily.visuelt } },
                error: error !== undefined,
              },
            }}
            value={value}
            onChange={(newValue) => {
              if (!newValue) return;
              const hours = newValue.getHours();
              callChange(
                `${p.prefix}.dateHour`,
                hours > 12 ? hours - 12 : hours,
              );
              callChange(`${p.prefix}.dateMinute`, newValue.getMinutes());
              callChange(`${p.prefix}.dateDayTime`, hours >= 12 ? 'PM' : 'AM');
            }}
          />
          <ErrorMessage content={error !== undefined ? String(error) : ''} />
        </Col>
      </LocalizationProvider>
    </>
  );
};
